import { KnowledgePageContent } from '@this+that/models';
import { StateCreator } from 'zustand';

import {
  BrainPageEntry,
  BrainPageSaveStatus,
  BrainPagesSlice,
  BrainStore,
  INITIAL_BRAIN_STATE,
} from '../types';

const EMPTY_PAGE_ENTRY: BrainPageEntry = {
  server: null,
  isLoading: false,
  error: null,
  draft: null,
  resetNonce: 0,
};

function isServerContentChanged(
  existingServer: KnowledgePageContent | null,
  incomingServer: KnowledgePageContent,
): boolean {
  if (!existingServer) {
    return false;
  }
  return (
    incomingServer.content !== existingServer.content ||
    incomingServer.versionInfo.version !== existingServer.versionInfo.version
  );
}

export const createPagesSlice: StateCreator<
  BrainStore,
  [],
  [],
  BrainPagesSlice
> = (set) => ({
  pagesById: INITIAL_BRAIN_STATE.pagesById,
  mobileBrainPageHeader: INITIAL_BRAIN_STATE.mobileBrainPageHeader,
  setMobileBrainPageHeader: (header) => set({ mobileBrainPageHeader: header }),

  setPageServer: (pageId, server) => {
    set((state) => {
      const existing = state.pagesById[pageId] ?? { ...EMPTY_PAGE_ENTRY };
      const resetNonce = isServerContentChanged(existing.server, server)
        ? existing.resetNonce + 1
        : existing.resetNonce;
      return {
        pagesById: {
          ...state.pagesById,
          [pageId]: { ...existing, server, isLoading: false, resetNonce },
        },
      };
    });
  },

  setPageTitle: (pageId, title) => {
    set((state) => {
      const entry = state.pagesById[pageId];
      if (!entry?.server) {
        return {};
      }
      return {
        pagesById: {
          ...state.pagesById,
          [pageId]: {
            ...entry,
            server: {
              ...entry.server,
              page: { ...entry.server.page, title },
            },
          },
        },
      };
    });
  },

  setPageLoading: (pageId, loading) => {
    set((state) => {
      const existing = state.pagesById[pageId] ?? { ...EMPTY_PAGE_ENTRY };
      return {
        pagesById: {
          ...state.pagesById,
          [pageId]: { ...existing, isLoading: loading },
        },
      };
    });
  },

  setPageError: (pageId, error) => {
    set((state) => {
      const entry = state.pagesById[pageId];
      if (!entry) {
        return {};
      }
      return {
        pagesById: {
          ...state.pagesById,
          [pageId]: { ...entry, error },
        },
      };
    });
  },

  removePageEntry: (pageId) => {
    set((state) => {
      const next = { ...state.pagesById };
      delete next[pageId];
      const removedPageIds = new Set(state.removedPageIds);
      removedPageIds.add(pageId);
      return { pagesById: next, removedPageIds };
    });
  },

  startDraft: (pageId) => {
    set((state) => {
      const entry = state.pagesById[pageId];
      if (!entry?.server || entry.draft) {
        return {};
      }
      return {
        pagesById: {
          ...state.pagesById,
          [pageId]: {
            ...entry,
            draft: {
              content: entry.server.content,
              baseVersion: entry.server.versionInfo.version,
              saveStatus: BrainPageSaveStatus.IDLE,
              lastSavedAt: null,
              error: null,
              conflict: null,
              contentRejection: null,
            },
          },
        },
      };
    });
  },

  setDraftContent: (pageId, content) => {
    set((state) => {
      const entry = state.pagesById[pageId];
      if (!entry?.draft) {
        return {};
      }
      return {
        pagesById: {
          ...state.pagesById,
          [pageId]: {
            ...entry,
            draft: { ...entry.draft, content, contentRejection: null },
          },
        },
      };
    });
  },

  revertDraft: (pageId) => {
    set((state) => {
      const entry = state.pagesById[pageId];
      if (!entry) {
        return {};
      }
      return {
        pagesById: {
          ...state.pagesById,
          [pageId]: { ...entry, draft: null },
        },
      };
    });
  },

  setDraftSaveStatus: (pageId, status) => {
    set((state) => {
      const entry = state.pagesById[pageId];
      if (!entry?.draft) {
        return {};
      }
      return {
        pagesById: {
          ...state.pagesById,
          [pageId]: {
            ...entry,
            draft: { ...entry.draft, saveStatus: status },
          },
        },
      };
    });
  },

  setDraftSaveError: (pageId, error) => {
    set((state) => {
      const entry = state.pagesById[pageId];
      if (!entry?.draft) {
        return {};
      }
      return {
        pagesById: {
          ...state.pagesById,
          [pageId]: {
            ...entry,
            draft: {
              ...entry.draft,
              saveStatus: BrainPageSaveStatus.ERROR,
              error,
            },
          },
        },
      };
    });
  },

  markDraftSaved: (pageId, newServer) => {
    set((state) => {
      const entry = state.pagesById[pageId];
      if (!entry) {
        return {};
      }
      return {
        pagesById: {
          ...state.pagesById,
          [pageId]: { ...entry, server: newServer, draft: null },
        },
      };
    });
  },

  rebaseDraftAfterSave: (pageId, newServer) => {
    set((state) => {
      const entry = state.pagesById[pageId];
      if (!entry?.draft) {
        return {};
      }
      return {
        pagesById: {
          ...state.pagesById,
          [pageId]: {
            ...entry,
            server: newServer,
            draft: {
              ...entry.draft,
              baseVersion: newServer.versionInfo.version,
              saveStatus: BrainPageSaveStatus.IDLE,
              lastSavedAt: Date.now(),
              error: null,
              conflict: null,
              contentRejection: null,
            },
          },
        },
      };
    });
  },

  setDraftConflict: (pageId, conflict) => {
    set((state) => {
      const entry = state.pagesById[pageId];
      if (!entry?.draft) {
        return {};
      }
      return {
        pagesById: {
          ...state.pagesById,
          [pageId]: {
            ...entry,
            draft: {
              ...entry.draft,
              conflict,
              saveStatus: BrainPageSaveStatus.ERROR,
            },
          },
        },
      };
    });
  },

  clearDraftConflict: (pageId) => {
    set((state) => {
      const entry = state.pagesById[pageId];
      if (!entry?.draft) {
        return {};
      }
      return {
        pagesById: {
          ...state.pagesById,
          [pageId]: {
            ...entry,
            draft: {
              ...entry.draft,
              conflict: null,
              saveStatus: BrainPageSaveStatus.IDLE,
            },
          },
        },
      };
    });
  },

  setDraftContentRejection: (pageId, issues) => {
    set((state) => {
      const entry = state.pagesById[pageId];
      if (!entry?.draft) {
        return {};
      }
      return {
        pagesById: {
          ...state.pagesById,
          [pageId]: {
            ...entry,
            draft: { ...entry.draft, contentRejection: issues },
          },
        },
      };
    });
  },

  clearDraftContentRejection: (pageId) => {
    set((state) => {
      const entry = state.pagesById[pageId];
      if (!entry?.draft) {
        return {};
      }
      return {
        pagesById: {
          ...state.pagesById,
          [pageId]: {
            ...entry,
            draft: { ...entry.draft, contentRejection: null },
          },
        },
      };
    });
  },
});
